import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../../../server/api/hooks/use-api-loader";
import {
  SlackEngineSevice,
  type SlackEngineV2BuildMessageRequest,
  type SlackEngineV2Message,
} from "../../../../../../../server/api/slack-engine-v2";
import { useToken } from "../../../../../../../server/auth/use-token";
import { useSupabaseLoader } from "../../../../../../../server/supabase/hooks";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import type { UUID } from "../../../../../../../utils/uuid";
import { useMessageBodyCreator } from "../../../../../../message/editor/utils/create-message-body/create-message-body";
import { useRecipientsForChannel } from "../../../../../../recipients/hooks/use-recipients-for-channel";
import { SupabaseSlackAliasService } from "../../../../../../slack/server/slack-supabase-service";
import type { PreviewSlackContext } from "../components/slack-wrapper/slack-wrapper";
import { usePreviewContext } from "../preview-context";
import { useEmailPreviewSource } from "./use-email-preview-source";
import { usePreviewTags } from "./use-preview-tags";
import { extractSurveyIdFromDesign, useSurveyPreviewData } from "./use-survey-preview-data";

export function useBuiltSlackMessage(): UseApiLoaderResponse<{
  data: SlackEngineV2Message["data"];
  context: PreviewSlackContext;
  alias?: { name: string; image: string };
}> {
  const getToken = useToken();

  const {
    moment_preview_data: { from, account_id, poll_ids, design, dynamic_segment_id_map },
    preview_info,
    preview_test_data,
  } = usePreviewContext();

  const {
    design: previewDesign,
    subject,
    additional_merge_fields: email_additional_merge_fields,
    use_unlayer_email_preview,
  } = useEmailPreviewSource();

  const { loading: aliasLoading, data: alias } = useSupabaseLoader(
    async ({ supabase }) => {
      if (!from) return null;
      const { data, error } = await new SupabaseSlackAliasService(supabase).maybeGet(from as UUID);
      if (error || !data) return null;
      return { name: data.name, image: data.image };
    },
    [from],
  );

  const { loading: createMessageBodyLoading, data: createMessageBody } = useMessageBodyCreator();

  const { loading: mergeFieldsLoading, data: mergeFields } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design: previewDesign,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
  });
  const { loading: recipientsLoading, data: recipients } = useRecipientsForChannel("slack", true);

  const surveyId = extractSurveyIdFromDesign(design);
  const { loading: surveyLoading, data: surveyData } = useSurveyPreviewData({
    body: surveyId ? `{{#each survey_result_${surveyId}}}` : undefined,
  });

  return useApiLoader(async () => {
    if (
      createMessageBodyLoading ||
      mergeFieldsLoading ||
      recipientsLoading ||
      aliasLoading ||
      surveyLoading
    )
      throw new Waiting();
    if (!createMessageBody) throw new Error("No message body creator");

    const token = await getToken();

    const { data: messageBodyData, errorMessage } = await createMessageBody({
      editorState: design,
      channel: "slack",
      format: "chat",
      account_id,
      poll_ids: poll_ids || [],
    });

    if (errorMessage || !messageBodyData?.body)
      throw new Error(errorMessage ?? "Error creating message body");

    const service = new SlackEngineSevice(token);

    const surveyMergeFields = surveyData
      ? ({ [surveyData.key]: surveyData.value } as unknown as Record<string, string | null>)
      : {};

    const response = await service.buildMessage({
      body: JSON.parse(messageBodyData.body) as SlackEngineV2BuildMessageRequest["body"],
      mail_merge_fields: {
        ...Object.fromEntries(
          (mergeFields?.tags ?? []).map((field) => [field.key, field.value ?? ""]),
        ),
        ...surveyMergeFields,
      },
      dynamic_condition_fields: mergeFields?.dynamic_condition_fields ?? {},
    });

    if (response.error || !response.data)
      throw new Error(response.error ?? "Error building message");
    return {
      data: response.data.data,
      context: {
        channels: recipients?.map((recipient) => ({
          id: recipient.destination,
          name: recipient.display || "⚠️ Channel not found", // The slack-jsx will present this
        })),
      } satisfies PreviewSlackContext,
      ...(alias ? { alias } : {}),
    };
  }, [
    createMessageBodyLoading,
    mergeFieldsLoading,
    recipientsLoading,
    aliasLoading,
    createMessageBody,
    getToken,
    design,
    account_id,
    recipients,
    mergeFields?.tags,
    mergeFields?.dynamic_condition_fields,
    poll_ids,
    alias,
    surveyLoading,
    surveyData,
  ]);
}
